import React, { useMemo } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Avatar, Box, Button, Card, Highlight, Icon, Tooltip } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { PortfolioStatus } from 'types/rewards'
import { useRewardsSheetContext } from '../context/RewardsSheetProvider'
import { formatAmount } from '../utils'

const UNAVAILABLE_PLACEHOLDER = '—'

export interface RewardsAssetProps {
  currency: string
  apyRate?: string
  availableAmount?: {
    value: number
    loading: boolean
  }
  onDeposit?: () => void
  comingSoon?: boolean
}

export const RewardsAsset = ({
  currency,
  apyRate,
  availableAmount,
  onDeposit,
  comingSoon = false,
}: RewardsAssetProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { userPortfolios } = useRewardsSheetContext()

  const portfolioForAsset = userPortfolios?.find(
    portfolio => portfolio?.currency === currency && portfolio?.status === PortfolioStatus.ACTIVE
  )
  const hasAllocation = !!portfolioForAsset

  const handleSeeDetails = (currency: string) => {
    navigate(`${PathNames.REWARDS}/${currency}`)
  }

  const AvailableAmount = useMemo(() => {
    if (comingSoon) return UNAVAILABLE_PLACEHOLDER

    if (hasAllocation) {
      return formatAmount(Number.parseFloat(portfolioForAsset?.balance?.total ?? '0'))
    }
    if (availableAmount?.loading) {
      return (
        <Box flex align="center" justify="center" className="animate-spin mt-2">
          <Icon name="Spinner" size="small" />
        </Box>
      )
    }
    return availableAmount?.value ?? UNAVAILABLE_PLACEHOLDER
  }, [
    comingSoon,
    hasAllocation,
    portfolioForAsset?.balance?.total,
    availableAmount?.loading,
    availableAmount?.value,
  ])

  const displayApyRate = comingSoon ? UNAVAILABLE_PLACEHOLDER : apyRate

  const rightHeaderAction = comingSoon ? (
    <Highlight type="feature" variant="emphasis">
      {t('rewards.comingSoon').toUpperCase()}
    </Highlight>
  ) : !hasAllocation ? (
    <Tooltip bodyContent={t('rewards.allocateCurrency')}>
      <Button
        iconElement={<Icon name="Plus" size="large" />}
        variant="ghost"
        noPadding
        onClick={e => {
          e.stopPropagation()
          onDeposit?.()
        }}
        className="underline"
      />
    </Tooltip>
  ) : undefined

  const handleCardClick = () => {
    if (comingSoon) return
    handleSeeDetails(currency)
  }

  return (
    <Card
      variant="interactive"
      className={clsx({
        'hover:border-none! active:border-none! cursor-auto! hover:shadow-none! active:shadow-none!':
          comingSoon,
      })}
      borderRadius={12}
      style={comingSoon ? { cursor: 'default' } : undefined}
      full
      onClick={handleCardClick}
    >
      <Box flexCol gapY={8}>
        <Box flex align="center" justify="between" width="full">
          <Box flex align="center" gap={8}>
            <Avatar name={currency} size="small" />
            <h3 className="text-h3 text-foreground-prominent">{currency}</h3>
          </Box>
          {rightHeaderAction}
        </Box>

        <Box flex align="start" justify="between" width="full" gap={16}>
          <Box flexCol align="start">
            <p className="text-body-strong text-foreground-prominent">{displayApyRate}</p>
            <p className="text-body-regular-small text-foreground-subdued">
              {t('rewards.rewardsRate')}
            </p>
          </Box>

          <Box flexCol align="start">
            <p className="text-body-regular-small text-foreground-subdued">
              {hasAllocation ? t('rewards.allocatedFunds') : t('rewards.availableToAllocate')}
            </p>
            <p className="text-body-strong text-foreground-prominent w-full text-left">
              {AvailableAmount}
            </p>
          </Box>
        </Box>
      </Box>
    </Card>
  )
}
